// @ts-nocheck

import { map, memoize, partition, reduce } from 'lodash';

import basketActions from '../actions/basket';
import { Target } from '../client';
import emptyFunction from '../lib/core/emptyFunction';
import { BasketActionTypes } from '../types/actions/basket';
import { MyMatchActionTypes } from '../types/actions/mymatch';
import { BasketModes } from '../types/basket';
import NumberUtils from '../utils/number';

const hookActionTypes = [
  BasketActionTypes.ADD_ITEM,
  BasketActionTypes.ADD_BETBUILDER_ITEM,
  BasketActionTypes.DEL_ITEM,
  BasketActionTypes.GET_SELECTION,
  BasketActionTypes.RECEIVE_BOOSTERS,
  BasketActionTypes.SET_LOADING_OK,

  BasketActionTypes.SWITCH_MODE,
  BasketActionTypes.SYSTEM_ITEM_SELECTION_CHANGE,
  BasketActionTypes.SYSTEM_ITEM_SELECTION_RESET,
  BasketActionTypes.ITEM_BANKER_CHANGE,
  BasketActionTypes.STAKE_CHANGE,

  BasketActionTypes.SELECTED_FREEBET_CHANGE,
  BasketActionTypes.SELECTED_BONUS_CHANGE,
  BasketActionTypes.REFRESH,

  BasketActionTypes.SET_CASHOUT,
  BasketActionTypes.VALIDATE_ITEMS,

  MyMatchActionTypes.MERGE,
  MyMatchActionTypes.SPLIT,
];

const translateModeToBetslipType = (mode) => {
  switch (mode) {
    case BasketModes.SIMPLE:
      return 1;
    case BasketModes.COMBINED:
      return 2;
    case BasketModes.SYSTEM:
      return 3;
    default:
      return null;
  }
};

const createRule = (rule) => {
  try {
    if (Target.__ENV__ !== 'production') {
      // eslint-disable-next-line no-new-func
      return new Function(
        'betslip',
        'betslipType',
        `console.lo = k => k;\r\n${rule.replace(/console\.log/g, 'console.lo')}`,
      );
    }
    // eslint-disable-next-line no-new-func
    return new Function('betslip', 'betslipType', rule);
  } catch (error) {
    console.error(error);
    return emptyFunction;
  }
};

const getRuleFunction = (rule) => createRule(rule);
const getMemoizedRuleFunction = memoize(getRuleFunction);

export default ({ dispatch, getState }) =>
  (next) =>
  (action) => {
    if (hookActionTypes.indexOf(action.type) !== -1) {
      const returnValue = next(action);

      const { basket, entities } = getState();
      const { boosters, systems, mode, stake } = basket;
      const { matches, bets, outcomes } = entities;

      if (mode !== BasketModes.SIMPLE) {
        // payload
        const enhancedItems = map(basket.items, (item) => {
          const match = matches[item.matchId];
          const bet = bets[item.betId];
          const outcome = outcomes[item.oddId];
          const odd = item.validatedValue || item.value;
          if (item.betbuilder) {
            if (match) {
              return {
                item,
                match,
                bet: {},
                outcome: {
                  odd: item.value,
                },
              };
            }
            return null;
          } else {
            if (match && bet && outcome && odd) {
              return {
                item,
                match: matches[item.matchId],
                bet: bets[item.betId],
                outcome: {
                  ...outcomes[item.oddId],
                  odd,
                },
              };
            }
            return null;
          }
        }).filter((k) => k);

        let eligibleBooster = null;

        if (!basket.selectedBonusId) {
          if (mode === BasketModes.COMBINED) {
            // booster combined
            eligibleBooster = reduce(
              boosters,
              (prev, booster) => {
                const { boosterId, rule, deleted } = booster;

                if (deleted) return prev;

                const ruleFunction = getRuleFunction(rule);
                let result = null;
                try {
                  result = ruleFunction(enhancedItems, translateModeToBetslipType(mode));
                } catch (error) {
                  console.error('error with booster: ', boosterId);
                }

                if (result) {
                  const current = +result.current || 1;
                  const next = +result.next || 1;
                  const live = result.live;
                  console.debug(`%c booster %O`, 'background: #616161; color: #fff;', {
                    id: boosterId,
                    current,
                    next,
                    live,
                  });
                  if (current > 1 || next > 1) {
                    if (!prev || prev.current < current) {
                      const percent = (result.current * 100 - 100) / 100;
                      const totalOdd = reduce(enhancedItems, (prev, curr) => curr.outcome.odd * prev, 1);
                      return {
                        boosterId,
                        current,
                        boosterGain: Math.round(Math.max(0, totalOdd - 1) * Math.max(0, stake) * percent * 100) / 100,
                        next: prev && prev.next > next ? prev.next : next,
                        live: prev && prev.next > next ? prev.live : live,
                      };
                    }
                  }
                }
                return prev;
              },
              null,
            );
          } else {
            // booster system
            const partitionedItems = partition(enhancedItems, (x) => x.item.banker);
            const [bankerItems, noBankerItems] = partitionedItems;

            // k parmis nobankers
            const combinaisons = reduce(
              systems,
              (allCombinations, system) => {
                if (system.checked) {
                  allCombinations = allCombinations.concat(NumberUtils.getCombinations(noBankerItems, system.k));
                }
                return allCombinations;
              },
              [],
            ).map((x) => x.concat(bankerItems));

            eligibleBooster = reduce(
              boosters,
              (prev, booster) => {
                const { boosterId, rule, deleted } = booster;

                if (deleted) return prev;

                const ruleFunction = getMemoizedRuleFunction(rule);
                const boosterGain = reduce(
                  combinaisons,
                  (finalGain, comb) => {
                    let result = null;
                    try {
                      result = ruleFunction(comb, translateModeToBetslipType(mode));
                    } catch (error) {
                      console.error('error with booster: ', boosterId);
                    }

                    if (result) {
                      const current = +result.current || 1;
                      if (current > 1) {
                        const percent = (result.current * 100 - 100) / 100;
                        const totalOdd = reduce(comb, (prev, curr) => curr.outcome.odd * prev, 1);
                        return (
                          finalGain +
                          Math.round(Math.max(0, totalOdd - 1) * Math.max(0, parseFloat(stake)) * percent * 100) / 100
                        );
                      }
                    }
                    return finalGain;
                  },
                  0,
                );
                console.debug(`%c booster %O`, 'background: #616161; color: #fff;', {
                  id: boosterId,
                  gain: boosterGain,
                });
                if (!prev || prev.boosterGain < boosterGain) {
                  return {
                    boosterId,
                    boosterGain: Math.round(boosterGain * 100) / 100,
                  };
                }
                return prev;
              },
              null,
            );
          }
        }

        dispatch(basketActions.basketSetBooster(eligibleBooster));
      }
      return returnValue;
    }
    return next(action);
  };
